import { useEffect, useState } from 'react'
import { fetchGeo, type GeoResponse } from '../../lib/api'
import { useFlowFilter } from '../FlowFilterContext'

interface Props { range: { from: number; to: number }; limit?: number }

const fmt = (n: number) => n.toLocaleString('da-DK')
const fmtPct = (p: number) => `${(p * 100).toFixed(0)}%`

export function GeoTable({ range, limit = 25 }: Props) {
  const { variantParam, audienceParam } = useFlowFilter()
  const [data, setData] = useState<GeoResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setData(null); setError(null)
    fetchGeo(range, variantParam, limit, audienceParam)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(String(e)) })
    return () => { cancelled = true }
  }, [range.from, range.to, variantParam, limit, audienceParam])

  if (error) return <div className="card card-empty">Kunne ikke hente geo-data</div>
  if (!data) return <div className="card card-skeleton" aria-busy />
  if (data.total === 0) return (
    <div className="card card-empty"><div className="card-title">Geografi</div><div className="muted">Ingen leads i perioden</div></div>
  )

  const coverage = data.total > 0 ? data.withPostnr / data.total : 0
  const max = Math.max(1, ...data.rows.map((r) => r.n))

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Geografi (postnr)</div>
        <div className="card-subtitle">{data.rows.length === 0 ? 'Ingen' : `Top ${data.rows.length}`} områder</div>
      </div>
      <div className="card-note small">
        {fmtPct(coverage)} af leads har postnr ({fmt(data.withPostnr)} af {fmt(data.total)})
      </div>
      <table className="data-table compact">
        <thead>
          <tr>
            <th>Postnr</th>
            <th>By</th>
            <th>Leads</th>
            <th aria-label="visualisering" />
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={`${r.postnr}-${r.by ?? ''}`}>
              <td className="num">{r.postnr}</td>
              <td>{r.by ?? '—'}</td>
              <td className="num">{fmt(r.n)}</td>
              <td className="bar-cell">
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: `${(r.n / max) * 100}%` }} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
